// Shot-by-shot score entry for phones (the "Tap" arm of the A/B test against
// NumericKeypad). The shooter taps the value of each shot on the target; the
// stage Score and X count compute themselves — no arithmetic, no typing.
//
// A bullseye stage is 10 shots. Values: X (a 10 in the X-ring), 10..1, and M
// (miss, 0). Score = sum of shot values (X = 10); X count = number of X's.
// The parent owns the shot list and the commit; this component is presentation
// plus tap handlers, and (like NumericKeypad) never steals focus.

const SHOTS_PER_STAGE = 10;

// Ordered high→low so the common values sit under the thumb first. Below the
// 5-ring counts as a miss for fast entry, so the chips are X, 10..5, and M.
const VALUES = [
  { key: "X", val: 10, isX: true },
  { key: "10", val: 10, isX: false },
  { key: "9", val: 9, isX: false },
  { key: "8", val: 8, isX: false },
  { key: "7", val: 7, isX: false },
  { key: "6", val: 6, isX: false },
  { key: "5", val: 5, isX: false },
  { key: "M", val: 0, isX: false },
];

const noFocusSteal = (e) => e.preventDefault();

const TapPad = ({
  context,
  shots = [],
  onShot,
  onUndo,
  onClear,
  onNext,
  onDone,
  onSwitchMode,
}) => {
  const score = shots.reduce((a, s) => a + s.val, 0);
  const xCount = shots.filter((s) => s.isX).length;
  const full = shots.length >= SHOTS_PER_STAGE;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-brand-200 bg-white/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-pop backdrop-blur sm:hidden"
      role="group"
      aria-label="Shot-by-shot score entry"
    >
      {/* Readout: stage context + the running total that fills itself in. */}
      <div className="mb-2 flex items-end justify-between gap-3">
        <div className="min-w-0">
          {context && (
            <div className="truncate text-xs font-medium text-ink-500">
              {context}
            </div>
          )}
          <div className="text-sm font-semibold text-ink-700">
            Shot {Math.min(shots.length + 1, SHOTS_PER_STAGE)} of {SHOTS_PER_STAGE}
          </div>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="tabular-nums text-3xl font-bold leading-none text-brand-700">
            {score}
          </span>
          <span className="text-sm font-semibold text-ink-500">· {xCount}X</span>
        </div>
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={noFocusSteal}
          onClick={onDone}
          className="btn-ghost btn-sm shrink-0"
        >
          Done
        </button>
      </div>

      {/* Shot progress: one pip per shot, filled as the string is entered. */}
      <div className="mb-3 flex items-center gap-1.5">
        {Array.from({ length: SHOTS_PER_STAGE }).map((_, i) => {
          const s = shots[i];
          return (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                s ? (s.isX ? "bg-brand-600" : "bg-brand-300") : "bg-ink-200"
              }`}
              aria-hidden="true"
            />
          );
        })}
      </div>

      {/* Value chips: tap the value of the shot just fired. */}
      <div className="grid grid-cols-4 gap-2">
        {VALUES.map((v) => {
          const tone = v.isX
            ? "bg-brand-600 text-white ring-brand-600 hover:bg-brand-700"
            : v.key === "M"
            ? "bg-white text-ink-400 ring-ink-200 hover:bg-ink-50"
            : "bg-white text-ink-900 ring-ink-200 hover:bg-ink-50";
          return (
            <button
              key={v.key}
              type="button"
              tabIndex={-1}
              disabled={full}
              aria-label={
                v.isX ? "X ring" : v.key === "M" ? "Miss" : `Value ${v.key}`
              }
              onMouseDown={noFocusSteal}
              onClick={() => onShot(v.val, v.isX)}
              className={`flex h-14 select-none items-center justify-center rounded-xl text-xl font-semibold
                ring-1 ring-inset transition-transform duration-75 active:scale-[0.96]
                disabled:opacity-40 disabled:active:scale-100 ${tone}`}
            >
              {v.key}
            </button>
          );
        })}
      </div>

      {/* Undo the last shot; Next carries to the following stage. */}
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={noFocusSteal}
          onClick={onSwitchMode}
          className="btn-ghost btn-sm shrink-0"
          aria-label="Switch to keypad entry"
        >
          Keypad
        </button>
        <button
          type="button"
          tabIndex={-1}
          disabled={shots.length === 0}
          onMouseDown={noFocusSteal}
          onClick={onUndo}
          className="flex h-12 flex-1 select-none items-center justify-center rounded-xl bg-white text-base font-semibold text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-ink-50 active:scale-[0.98] disabled:opacity-40"
        >
          ⌫ Undo
        </button>
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={noFocusSteal}
          onClick={onNext}
          className="flex h-12 flex-[2] select-none items-center justify-center rounded-xl bg-brand-600 text-base font-semibold text-white ring-1 ring-inset ring-brand-600 hover:bg-brand-700 active:scale-[0.98]"
        >
          Next stage ›
        </button>
      </div>
    </div>
  );
};

export { SHOTS_PER_STAGE };
export default TapPad;
